/**
 * 워크스페이스 보안·공유 정책 초안 (PRD2 T-3.18).
 * 느슨해지는 변경은 확인 문장이 다르다.
 */

export type WorkspacePolicy = {
  domain: string;
  /** 새로 보내는 초대의 유효 기간. 이미 보낸 초대의 만료일은 바꾸지 않는다. */
  inviteDays: number;
  sessionDays: number;
  externalShare: boolean;
};

const DEFAULT_POLICY: WorkspacePolicy = {
  domain: "wavex.local",
  inviteDays: 7,
  sessionDays: 30,
  externalShare: false,
};

let policy: WorkspacePolicy = { ...DEFAULT_POLICY };

export function resetWorkspacePolicy(): void {
  policy = { ...DEFAULT_POLICY };
}

/** 허용 도메인이 있고 외부 공유가 꺼져 있을 때, 그 도메인 밖 주소. */
export function emailOutsideDomain(email: string, policy: WorkspacePolicy = getWorkspacePolicy()): boolean {
  const domain = policy.domain.trim().toLowerCase();
  if (!domain || policy.externalShare) return false;
  const host = email.trim().toLowerCase().split("@")[1] ?? "";
  return host !== "" && host !== domain;
}

export function getWorkspacePolicy(): WorkspacePolicy {
  return { ...policy };
}

export function policyDraftError(draft: WorkspacePolicy): string | null {
  const domain = draft.domain.trim();
  if (domain && !/^[a-z0-9.-]+$/.test(domain)) return "도메인은 영문과 점만 씁니다.";
  if (!Number.isInteger(draft.inviteDays) || draft.inviteDays < 1 || draft.inviteDays > 30) {
    return "초대 유효 기간은 1일에서 30일 사이입니다.";
  }
  if (draft.sessionDays < 7 || draft.sessionDays > 90) return "세션은 7일에서 90일 사이입니다.";
  return null;
}

/** 도메인을 비우거나, 초대·세션을 늘리거나, 외부 공유를 켜면 느슨해진다. */
export function policyRelaxed(prev: WorkspacePolicy, next: WorkspacePolicy): boolean {
  if (prev.domain.trim() && !next.domain.trim()) return true;
  if (next.inviteDays > prev.inviteDays) return true;
  if (next.sessionDays > prev.sessionDays) return true;
  if (!prev.externalShare && next.externalShare) return true;
  return false;
}

/** 바뀐 항목만. 그대로면 빈 문자열이다. */
export function policyChangeDetail(prev: WorkspacePolicy, next: WorkspacePolicy): string {
  const parts: string[] = [];
  const domain = next.domain.trim();
  if (prev.domain.trim() !== domain) parts.push(domain ? `허용 도메인 ${domain}` : "허용 도메인 없음");
  if (prev.inviteDays !== next.inviteDays) parts.push(`초대 유효 기간 ${next.inviteDays}일`);
  if (prev.sessionDays !== next.sessionDays) parts.push(`세션 ${next.sessionDays}일`);
  if (prev.externalShare !== next.externalShare) parts.push(next.externalShare ? "외부 공유 켬" : "외부 공유 끔");
  return parts.join(", ");
}

export function applyWorkspacePolicy(next: WorkspacePolicy): boolean {
  if (policyDraftError(next)) return false;
  policy = {
    domain: next.domain.trim(),
    inviteDays: next.inviteDays,
    sessionDays: next.sessionDays,
    externalShare: next.externalShare,
  };
  return true;
}
